import { motion, AnimatePresence } from "framer-motion";
import {
  Users,
  Search,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Mail,
  Phone,
  Instagram,
  Calendar,
  CreditCard,
  MapPin,
  Check,
  X,
  ArrowUpDown,
  UserCircle,
  Copy,
  Trash2,
  AlertTriangle,
  Edit3,
  ArrowRightLeft,
  UserX,
  Save,
  XCircle,
  ExternalLink,
  Gift,
  Plus,
  GitMerge,
  Smartphone,
  MessageSquare,
  Star,
  RefreshCw,
  History,
  Shield,
  CalendarClock,
  Clock,
  Flame,
} from "lucide-react";

// Apple icon SVG component
const AppleIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 384 512" fill="currentColor" className={className}>
    <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z"/>
  </svg>
);

// Google Play icon SVG component
const PlayStoreIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 512 512" fill="currentColor" className={className}>
    <path d="M325.3 234.3L104.6 13l280.8 161.2-60.1 60.1zM47 0C34 6.8 25.3 19.2 25.3 35.3v441.3c0 16.1 8.7 28.5 21.7 35.3l256.6-256L47 0zm425.2 225.6l-58.9-34.1-65.7 64.5 65.7 64.5 60.1-34.1c18-14.3 18-46.5-1.2-60.8zM104.6 499l280.8-161.2-60.1-60.1L104.6 499z"/>
  </svg>
);
import { useState, useMemo, useEffect, useRef } from "react";
import { useSearchParams } from "wouter";
import { ROLE_CONFIG, ALL_ROLES } from "../components/roleConfig";
import { RoleBadge } from "../components/RoleBadge";
import { useAdminUsersDetailed, useAdminUserDetail, useDeleteAdminUser, useMergeUsers, useUpdateAdminBooking, useGrantEntitlement, useUpdateEntitlement, useCreateAdminBooking, useRefreshEntitlements, useUpdateUserRole, useExtendSubscription, useAdminUserNotes } from "../api/useAdminUsers";
import { stripeCustomerUrl } from '../utils/stripeLinks';
import { firebaseAuthUsersUrl } from '../utils/firebaseLinks';
import { UserNotesSection } from "../components/UserNotesSection";
import { useAdminNeighborhoods } from "../api/useAdminNeighborhoods";
import { useAdminEvents } from "../api/useAdminEvents";
import { ReassignmentDialog } from "../components/ReassignmentDialog";
import { AuditLogModal } from "../components/AuditLogModal";
import { EditReservationModal } from "../components/EditReservationModal";
import { ExtendSubscriptionModal } from "../components/ExtendSubscriptionModal";
import { SendMessageButton } from "../components/SendMessageButton";
import { useToast } from "@/components/ux/Toast";
import { useConfirm } from "@/components/ConfirmModal";
import { useOpenNoShowCase } from "../api/useAdminNoShowFees";
import { AdminAPI } from "@shared/types/api";
import { useAllQuizQuestions } from "@/data/quiz";
import { QuizQuestion } from "@shared/types";
import { formatShortDate, formatShortDateTime } from "@/utils/dateUtils";
import { ProfileImage } from "@/components/ProfileImage";
import { Loader } from '@/components/Loader';

const REVENUECAT_PROJECT_ID = "2072286d";

// Helper function for payment processor icons
function getPaymentProcessorIcon(processor: string) {
  switch (processor) {
    case "APP_STORE":
      return <AppleIcon className="w-5 h-5 text-accent-500" />;
    case "PLAY_STORE":
      return <PlayStoreIcon className="w-5 h-5 text-accent-500" />;
    case "STRIPE":
    default:
      return <CreditCard className="w-5 h-5 text-accent-500" />;
  }
}

type SortField = "createdAt" | "firstName" | "totalBookings" | "hasActiveSubscription";
type SortOrder = "asc" | "desc";

// Compact overflow menu shown in the user detail panel — set one of the roles
// or clear it. Closes on outside click / Escape.
function RoleControl({ userId, role }: { userId: string; role: AdminAPI.UserRole | null }) {
  const updateRole = useUpdateUserRole();
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const handleSetRole = async (next: AdminAPI.UserRole | null) => {
    setOpen(false);
    if (next === (role ?? null) || updateRole.isPending) return;
    try {
      await updateRole.mutateAsync({ userId, role: next });
      toast.success(next ? `Role set to ${ROLE_CONFIG[next].label}` : "Role removed");
    } catch (error: any) {
      toast.error(error?.message || "Failed to update role");
    }
  };

  const current = role ? ROLE_CONFIG[role] : null;
  const CurrentIcon = current?.icon;

  return (
    <div className="relative inline-block" ref={menuRef}>
      <button
        onClick={(e) => {
          e.stopPropagation();
          setOpen((o) => !o);
        }}
        disabled={updateRole.isPending}
        className="flex items-center gap-2 px-3 py-1.5 bg-base-content/10 text-base-content rounded-lg text-sm font-medium hover:bg-base-content/20 transition-colors disabled:opacity-50"
        title="Set staff role"
      >
        {current && CurrentIcon ? (
          <CurrentIcon className={`w-4 h-4 ${current.className}`} />
        ) : (
          <Shield className="w-4 h-4" />
        )}
        {current ? current.label : "No role"}
        <ChevronDown className="w-3.5 h-3.5" />
      </button>

      {open && (
        <div className="absolute right-0 z-20 mt-1 w-44 rounded-lg border border-dark-border bg-dark-elevated shadow-2xl py-1">
          {ALL_ROLES.map((r) => {
            const config = ROLE_CONFIG[r];
            const Icon = config.icon;
            const isActive = role === r;
            return (
              <button
                key={r}
                onClick={(e) => {
                  e.stopPropagation();
                  handleSetRole(r);
                }}
                className="flex w-full items-center gap-2 px-3 py-2 text-sm text-left hover:bg-dark-border/50 transition-colors"
              >
                <Icon className={`w-4 h-4 ${config.className}`} />
                <span className="flex-1">{config.label}</span>
                {isActive && <Check className="w-3.5 h-3.5 text-green-500" />}
              </button>
            );
          })}
          <div className="my-1 border-t border-dark-border" />
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleSetRole(null);
            }}
            disabled={!role}
            className="flex w-full items-center gap-2 px-3 py-2 text-sm text-left text-dark-muted hover:bg-red-500/10 hover:text-red-400 transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent"
          >
            <X className="w-4 h-4" />
            <span className="flex-1">Remove role</span>
          </button>
        </div>
      )}
    </div>
  );
}

// Booking timestamps are instants; show them in the dinner's own city timezone
// (falling back to the admin's local zone when the booking has no city) so a
// "cancelled at 11pm" reads the way the guest actually experienced it.
function formatInEventZone(value: string | Date, timeZone: string | null): string {
  try {
    return new Intl.DateTimeFormat("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      timeZone: timeZone ?? undefined,
      timeZoneName: "short",
    }).format(new Date(value));
  } catch {
    return formatShortDateTime(value);
  }
}

// How far the moment sits from the dinner, e.g. "2d 4h before dinner" — this is
// what says whether a cancellation was a courtesy or a no-show-in-effect.
function formatRelativeToEvent(value: string | Date, eventStartsAt: string | Date): string {
  const diffMs = new Date(eventStartsAt).getTime() - new Date(value).getTime();
  if (!Number.isFinite(diffMs)) return "";
  const suffix = diffMs < 0 ? "after dinner" : "before dinner";
  const minutes = Math.round(Math.abs(diffMs) / 60000);
  if (minutes < 60) return `${minutes}m ${suffix}`;
  const hours = Math.floor(minutes / 60);
  if (hours < 48) return `${hours}h ${suffix}`;
  const days = Math.floor(hours / 24);
  const remainingHours = hours % 24;
  return `${days}d${remainingHours ? ` ${remainingHours}h` : ""} ${suffix}`;
}

// Format quiz answer value to human-readable label
function formatQuizAnswer(questionId: string, value: any, quizQuestions: QuizQuestion[]): string {
  if (!value) return "—";
  
  const question = quizQuestions.find((q: QuizQuestion) => q.id === questionId);
  if (!question) return String(value);
  
  if (Array.isArray(value)) {
    return value
      .map((v: string) => {
        const option = question.options?.find((o) => o.value === v);
        return option ? `${option.emoji || ""} ${option.label}`.trim() : v;
      })
      .join(", ");
  }
  
  if (question.type === "scale") {
    return `${value}/7`;
  }
  
  const option = question.options?.find((o) => o.value === value);
  return option ? `${option.emoji || ""} ${option.label}`.trim() : String(value);
}

// Delete Confirmation Modal Component
function DeleteUserModal({
  user,
  onClose,
  onConfirm,
  isDeleting,
}: {
  user: { id: string; firstName: string; lastName: string | null; email: string | null };
  onClose: () => void;
  onConfirm: () => void;
  isDeleting: boolean;
}) {
  const [confirmText, setConfirmText] = useState("");
  const isConfirmValid = confirmText.toLowerCase() === "delete";

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
        className="card border border-dark-border rounded-xl max-w-md w-full p-6 shadow-2xl"
      >
        {/* Warning Icon */}
        <div className="flex items-center justify-center w-14 h-14 mx-auto mb-4 rounded-full bg-red-500/20">
          <AlertTriangle className="w-7 h-7 text-red-500" />
        </div>

        <h3 className="text-xl font-bold text-center mb-2">Delete User</h3>
        <p className="text-dark-muted text-center text-sm mb-6">
          This action cannot be undone. This will permanently delete the user and all associated data.
        </p>

        {/* User Info */}
        <div className="bg-dark-bg/50 rounded-lg p-4 mb-6 space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-dark-muted">Name:</span>
            <span className="font-medium">{user.firstName} {user.lastName}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-dark-muted">Email:</span>
            <span className="font-mono text-xs">{user.email || "—"}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-dark-muted">User ID:</span>
            <span className="font-mono text-xs truncate max-w-[180px]">{user.id}</span>
          </div>
        </div>

        {/* Confirmation Input */}
        <div className="mb-6">
          <label className="block text-sm text-dark-muted mb-2">
            Type <span className="font-mono text-red-400">delete</span> to confirm:
          </label>
          <input
            type="text"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder="delete"
            className="w-full px-4 py-3 bg-dark-border/50 border border-dark-border rounded-lg text-sm focus:outline-none focus:border-red-500 transition-colors"
            autoFocus
            disabled={isDeleting}
          />
        </div>

        {/* Actions */}
        <div className="flex gap-3">
          <button
            onClick={onClose}
            disabled={isDeleting}
            className="flex-1 px-4 py-3 bg-dark-border text-white rounded-lg font-medium hover:bg-dark-border/70 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={!isConfirmValid || isDeleting}
            className="flex-1 px-4 py-3 bg-red-500 text-white rounded-lg font-medium hover:bg-red-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {isDeleting ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Deleting...
              </>
            ) : (
              <>
                <Trash2 className="w-4 h-4" />
                Delete User
              </>
            )}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}

// Create Reservation Modal Component
function CreateReservationModal({
  userId,
  onClose,
  onSuccess,
}: {
  userId: string;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const { data: eventsData, isLoading: eventsLoading } = useAdminEvents();
  const { data: neighborhoodsData, isLoading: neighborhoodsLoading } = useAdminNeighborhoods();
  const createBookingMutation = useCreateAdminBooking();
  
  const [selectedEventId, setSelectedEventId] = useState("");
  const [selectedNeighborhoodId, setSelectedNeighborhoodId] = useState("");
  const [status, setStatus] = useState<"active" | "pending">("active");

  const allEvents = eventsData?.data || [];
  const neighborhoods = neighborhoodsData?.data?.neighborhoods || [];
  
  // Filter to only show events within 24h grace period or later
  const cutoff = new Date();
  cutoff.setHours(cutoff.getHours() - 24);
  const events = allEvents.filter((event) => {
    const eventDate = new Date(event.date);
    return eventDate >= cutoff;
  });

  const handleSubmit = async () => {
    if (!selectedEventId || !selectedNeighborhoodId) return;
    
    try {
      await createBookingMutation.mutateAsync({
        userId,
        data: {
          eventTimeId: selectedEventId,
          neighborhoodId: selectedNeighborhoodId,
          status,
        },
      });
      onSuccess();
      onClose();
    } catch (error) {
      console.error("Failed to create booking:", error);
    }
  };

  const isValid = selectedEventId && selectedNeighborhoodId;
  const isLoading = eventsLoading || neighborhoodsLoading;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
        className="card border border-dark-border rounded-xl max-w-md w-full p-6 shadow-2xl"
      >
        {/* Header */}
        <div className="flex items-center justify-center w-14 h-14 mx-auto mb-4 rounded-full bg-primary-500/20">
          <Calendar className="w-7 h-7 text-primary-500" />
        </div>

        <h3 className="text-xl font-bold text-center mb-2">Create Reservation</h3>
        <p className="text-dark-muted text-center text-sm mb-6">
          Create a new reservation for this user
        </p>

        {isLoading ? (
          <div className="flex justify-center py-8">
            <Loader />
          </div>
        ) : (
          <div className="space-y-4">
            {/* Event Selection */}
            <div>
              <label className="block text-sm font-medium text-dark-muted mb-2">
                Event Date & Time
              </label>
              <select
                value={selectedEventId}
                onChange={(e) => setSelectedEventId(e.target.value)}
                className="w-full px-4 py-3 bg-dark-border/50 border border-dark-border rounded-lg text-sm focus:outline-none focus:border-primary-500 transition-colors"
              >
                <option value="">Select an event...</option>
                {events.map((event) => (
                  <option key={event.id} value={event.id}>
                    {event.date} at {event.time} ({event.activeBookingsCount} active bookings)
                  </option>
                ))}
              </select>
            </div>

            {/* Neighborhood Selection */}
            <div>
              <label className="block text-sm font-medium text-dark-muted mb-2">
                Neighborhood
              </label>
              <select
                value={selectedNeighborhoodId}
                onChange={(e) => setSelectedNeighborhoodId(e.target.value)}
                className="w-full px-4 py-3 bg-dark-border/50 border border-dark-border rounded-lg text-sm focus:outline-none focus:border-primary-500 transition-colors"
              >
                <option value="">Select a neighborhood...</option>
                {neighborhoods.map((n) => (
                  <option key={n.id} value={n.id}>
                    {n.name} ({n.cityName})
                  </option>
                ))}
              </select>
            </div>

            {/* Status Selection */}
            <div>
              <label className="block text-sm font-medium text-dark-muted mb-2">
                Status
              </label>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setStatus("active")}
                  className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    status === "active"
                      ? "bg-green-500/20 text-green-500 border border-green-500/50"
                      : "bg-dark-border text-dark-muted hover:bg-dark-border/70"
                  }`}
                >
                  Active
                </button>
                <button
                  type="button"
                  onClick={() => setStatus("pending")}
                  className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    status === "pending"
                      ? "bg-yellow-500/20 text-yellow-500 border border-yellow-500/50"
                      : "bg-dark-border text-dark-muted hover:bg-dark-border/70"
                  }`}
                >
                  Pending
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-3 mt-6">
          <button
            onClick={onClose}
            disabled={createBookingMutation.isPending}
            className="flex-1 px-4 py-3 bg-dark-border text-white rounded-lg font-medium hover:bg-dark-border/70 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!isValid || createBookingMutation.isPending || isLoading}
            className="flex-1 px-4 py-3 bg-primary-500 text-white rounded-lg font-medium hover:bg-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {createBookingMutation.isPending ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Creating...
              </>
            ) : (
              <>
                <Plus className="w-4 h-4" />
                Create Reservation
              </>
            )}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}

// Merge User Modal - merge source user's data into target user (options match accountMigration)
const MERGE_OPTIONS: { key: keyof AdminAPI.MergeUsersRequest; label: string; defaultOn?: boolean }[] = [
  { key: "mergeProfile", label: "Profile (name, email, phone, city, instagram, quiz)", defaultOn: false },
  { key: "mergeBookings", label: "Bookings" },
  { key: "mergeEntitlements", label: "Entitlements" },
  { key: "mergePayments", label: "Payments" },
  { key: "mergeReviews", label: "Reviews" },
  { key: "mergePostDinnerFeedback", label: "Post-dinner feedback" },
  { key: "mergeUserFeedback", label: "User feedback" },
  { key: "mergeUserConnections", label: "User connections" },
  { key: "mergeMutualConnections", label: "Mutual connections" },
  { key: "mergeUserDevices", label: "User devices" },
  { key: "mergePollResponses", label: "Poll responses" },
  { key: "mergeReferrals", label: "Referrals" },
  { key: "mergeCustomers", label: "Customers (Stripe)" },
  { key: "mergeRestaurantExclusions", label: "Restaurant exclusions" },
  { key: "mergeAutomationLogs", label: "Automation logs" },
  { key: "mergeFirestoreConversations", label: "Firestore conversations" },
  { key: "mergeCustomerIo", label: "Customer.io (merge profiles)" },
];

const DEFAULT_MERGE_OPTIONS: Record<string, boolean> = Object.fromEntries(
  MERGE_OPTIONS.map((o) => [o.key, o.defaultOn !== undefined ? o.defaultOn : true])
);

function MergeUserModal({
  sourceUser,
  onClose,
  onSuccess,
}: {
  sourceUser: { id: string; firstName: string; lastName: string | null; email: string | null };
  onClose: () => void;
  onSuccess: () => void;
}) {
  const toast = useToast();
  const [targetSearch, setTargetSearch] = useState("");
  const [targetUserId, setTargetUserId] = useState<string | null>(null);
  const [mergeOptions, setMergeOptions] = useState<Record<string, boolean>>(DEFAULT_MERGE_OPTIONS);

  const mergeMutation = useMergeUsers();
  const { data: searchData } = useAdminUsersDetailed({
    page: 1,
    limit: 50,
    search: targetSearch,
    sortField: "firstName",
    sortOrder: "asc",
    subscriptionFilter: "all",
  });

  const candidateUsers = (searchData?.data?.users ?? []).filter((u) => u.id !== sourceUser.id);
  const targetUser = candidateUsers.find((u) => u.id === targetUserId);

  const hasAnyOption = Object.entries(mergeOptions).some(([, v]) => v);

  const handleMerge = async () => {
    if (!targetUserId || !hasAnyOption) return;
    try {
      await mergeMutation.mutateAsync({
        sourceUserId: sourceUser.id,
        targetUserId,
        ...mergeOptions,
      } as AdminAPI.MergeUsersRequest);
      toast.success("User data merged successfully");
      onSuccess();
      onClose();
    } catch (err: unknown) {
      const message = err && typeof err === "object" && "message" in err ? String((err as { message: unknown }).message) : "Merge failed";
      toast.error(message);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
        className="card border border-dark-border rounded-xl max-w-lg w-full max-h-[90vh] overflow-hidden shadow-2xl flex flex-col"
      >
        <div className="p-6 overflow-y-auto flex-1">
          <div className="flex items-center justify-center w-14 h-14 mx-auto mb-4 rounded-full bg-primary-500/20">
            <GitMerge className="w-7 h-7 text-primary-500" />
          </div>
          <h3 className="text-xl font-bold text-center mb-2">Merge user into another</h3>
          <p className="text-dark-muted text-center text-sm mb-6">
            Move selected data from the source user into the target user. The source user is not deleted.
          </p>

          {/* Source user (read-only) */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-dark-muted mb-1">From (source)</label>
            <div className="px-4 py-3 bg-dark-border/30 rounded-lg border border-dark-border">
              <span className="font-medium">{sourceUser.firstName} {sourceUser.lastName}</span>
              {sourceUser.email && <span className="text-dark-muted text-sm ml-2">({sourceUser.email})</span>}
            </div>
          </div>

          {/* Target user search & select */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-dark-muted mb-1">Into (target user)</label>
            <input
              type="text"
              value={targetSearch}
              onChange={(e) => {
                setTargetSearch(e.target.value);
                if (!e.target.value) setTargetUserId(null);
              }}
              placeholder="Search by name, email, username..."
              className="w-full px-4 py-3 bg-dark-border/50 border border-dark-border rounded-lg text-sm focus:outline-none focus:border-primary-500 mb-2"
            />
            <div className="max-h-40 overflow-y-auto border border-dark-border rounded-lg divide-y divide-dark-border">
              {candidateUsers.length === 0 && (
                <div className="px-4 py-3 text-dark-muted text-sm">Type to search users</div>
              )}
              {candidateUsers.slice(0, 20).map((u) => (
                <button
                  key={u.id}
                  type="button"
                  onClick={() => setTargetUserId(u.id)}
                  className={`w-full text-left px-4 py-2.5 text-sm hover:bg-dark-border/50 transition-colors ${
                    targetUserId === u.id ? "bg-primary-500/20 text-primary-400" : ""
                  }`}
                >
                  {u.firstName} {u.lastName}
                  {(u.email || u.username) && (
                    <span className="text-dark-muted ml-2">— {u.email || `@${u.username}`}</span>
                  )}
                </button>
              ))}
            </div>
            {targetUser && (
              <p className="text-xs text-dark-muted mt-1">
                Selected: {targetUser.firstName} {targetUser.lastName}
              </p>
            )}
          </div>

          {/* What to merge */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-dark-muted mb-2">Data to merge (all default on)</label>
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {MERGE_OPTIONS.map(({ key, label }) => (
                <label key={key} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={mergeOptions[key] ?? true}
                    onChange={(e) => setMergeOptions((prev) => ({ ...prev, [key]: e.target.checked }))}
                    className="rounded border-dark-border bg-dark-border/50 text-primary-500 focus:ring-primary-500"
                  />
                  <span className="text-sm">{label}</span>
                </label>
              ))}
            </div>
          </div>
        </div>

        <div className="p-6 border-t border-dark-border flex gap-3">
          <button
            onClick={onClose}
            disabled={mergeMutation.isPending}
            className="flex-1 px-4 py-3 bg-dark-border text-white rounded-lg font-medium hover:bg-dark-border/70 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleMerge}
            disabled={!targetUserId || !hasAnyOption || mergeMutation.isPending}
            className="flex-1 px-4 py-3 bg-primary-500 text-white rounded-lg font-medium hover:bg-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {mergeMutation.isPending ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Merging...
              </>
            ) : (
              <>
                <GitMerge className="w-4 h-4" />
                Merge
              </>
            )}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}

// User Detail Panel Component
function UserDetailPanel({ userId, onUserDeleted }: { userId: string; onUserDeleted?: () => void }) {
  // Quiz questions with translations
  const quizQuestions = useAllQuizQuestions();

  const { data: userData, isLoading, refetch } = useAdminUserDetail(userId);
  const { confirm } = useConfirm();
  const openNoShowCase = useOpenNoShowCase();
  const { data: userNotesData } = useAdminUserNotes(userId);
  const userNotesCount = userNotesData?.data?.length ?? 0;
  const { data: neighborhoodsData } = useAdminNeighborhoods();
  const deleteUserMutation = useDeleteAdminUser();
  const updateBookingMutation = useUpdateAdminBooking();
  const grantEntitlementMutation = useGrantEntitlement();
  const updateEntitlementMutation = useUpdateEntitlement();
  const refreshEntitlementsMutation = useRefreshEntitlements();
  const extendSubscriptionMutation = useExtendSubscription();
  const toast = useToast();
  const [activeTab, setActiveTab] = useState<"bookings" | "payments" | "profile" | "devices" | "feedback" | "notes">("bookings");
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editingBooking, setEditingBooking] = useState<AdminAPI.AdminUserBooking | null>(null);
  const [editingEntitlementId, setEditingEntitlementId] = useState<string | null>(null);
  const [editingEntitlementStatus, setEditingEntitlementStatus] = useState<string>("");
  const [showCreateReservationModal, setShowCreateReservationModal] = useState(false);
  const [showMergeModal, setShowMergeModal] = useState(false);
  const [showAuditLogModal, setShowAuditLogModal] = useState(false);
  const [showExtendSubscriptionModal, setShowExtendSubscriptionModal] = useState(false);

  // Event data for editing a booking
  const { data: eventsData } = useAdminEvents();

  // Reassignment dialog (table moves / unassign / cant_be_seated)
  const [reassigningBooking, setReassigningBooking] =
    useState<AdminAPI.AdminUserBooking | null>(null);

  // Get neighborhoods list for dropdown
  const neighborhoods = neighborhoodsData?.data?.neighborhoods || [];

  const handleCopy = async (id: string, e?: React.MouseEvent) => {
    e?.stopPropagation();
    await navigator.clipboard.writeText(id);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const handleDeleteUser = async () => {
    try {
      await deleteUserMutation.mutateAsync(userId);
      setShowDeleteModal(false);
      onUserDeleted?.();
    } catch (error) {
      console.error("Failed to delete user:", error);
    }
  };

  const handleOpenEditBooking = (booking: AdminAPI.AdminUserBooking) => {
    setEditingBooking(booking);
  };

  const handleCloseEditBooking = () => {
    setEditingBooking(null);
  };

  const handleEditBookingSubmit = async (data: {
    eventTimeId?: string;
    neighborhoodId?: string;
    status?: "pending" | "active" | "cancelled" | "cant_be_seated";
  }) => {
    if (!editingBooking) return;
    const bookingId = editingBooking.id;

    try {
      const updates: AdminAPI.UpdateBookingRequest = {};
      if (data.eventTimeId) updates.eventTimeId = data.eventTimeId;
      if (data.neighborhoodId) updates.neighborhoodId = data.neighborhoodId;
      if (data.status) updates.status = data.status;

      if (Object.keys(updates).length > 0) {
        await updateBookingMutation.mutateAsync({ bookingId, data: updates });
        toast.success("Reservation updated");
        await refetch();
      }
      setEditingBooking(null);
    } catch (error: any) {
      toast.error(error?.message || "Failed to update reservation");
    }
  };

  const handleGrantEntitlement = async () => {
    try {
      await grantEntitlementMutation.mutateAsync(userId);
      refetch(); // Refresh user data to show new entitlement
    } catch (error) {
      console.error("Failed to grant entitlement:", error);
    }
  };

  const handleExtendSubscription = async (days: number) => {
    try {
      const result = await extendSubscriptionMutation.mutateAsync({ userId, days });
      toast.success(result.message || `Subscription extended by ${days} days`);
      setShowExtendSubscriptionModal(false);
      await refetch();
    } catch (error: any) {
      toast.error(error?.message || "Failed to extend subscription");
    }
  };

  const handleRefreshEntitlements = async () => {
    try {
      const result = await refreshEntitlementsMutation.mutateAsync(userId);
      toast.success(result.message || "Entitlements refreshed");
      await refetch();
    } catch (error) {
      toast.error("Failed to refresh entitlements from RevenueCat");
    }
  };

  const handleStartEditEntitlement = (entitlement: { id: string; status: string }) => {
    setEditingEntitlementId(entitlement.id);
    setEditingEntitlementStatus(entitlement.status);
  };

  const handleCancelEditEntitlement = () => {
    setEditingEntitlementId(null);
    setEditingEntitlementStatus("");
  };

  const handleSaveEntitlement = async (entitlementId: string) => {
    try {
      await updateEntitlementMutation.mutateAsync({
        entitlementId,
        status: editingEntitlementStatus,
      });
      refetch();
      handleCancelEditEntitlement();
    } catch (error) {
      console.error("Failed to update entitlement:", error);
    }
  };

  if (isLoading) {
    return (
      <div className="p-6 flex justify-center">
        <Loader />
      </div>
    );
  }

  const user = userData?.data;
  if (!user) return null;

  // Only Stripe-backed subscriptions can have their billing date moved; native
  // (App Store / Play Store) subscribers get a one-time entitlement instead.
  const extendableEntitlement = user.entitlements.find(
    (ent) =>
      ent.type === "subscription" &&
      !!ent.stripeSubscriptionId &&
      (ent.status === "active" || ent.status === "trialing")
  );

  return (
    <div className="p-6 bg-dark-bg/50 border-t border-divider">
      {/* User ID and Actions */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-4 text-sm">
          <div className="flex items-center gap-2">
            <span className="text-dark-muted">User ID:</span>
            <button
              onClick={(e) => handleCopy(userId, e)}
              className="flex items-center gap-1.5 px-2 py-1 bg-dark-border/50 rounded font-mono text-xs hover:bg-dark-border transition-colors"
              title="Click to copy"
            >
              {userId}
              {copiedId === userId ? (
                <Check className="w-3.5 h-3.5 text-green-500" />
              ) : (
                <Copy className="w-3.5 h-3.5 text-dark-muted" />
              )}
            </button>
          </div>
          {user.stripeCustomerId && (
            <a
              href={stripeCustomerUrl(user.stripeCustomerId)}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="flex items-center gap-1.5 px-2 py-1 bg-primary-500/20 text-primary-400 rounded text-xs font-medium hover:bg-primary-500/30 transition-colors"
            >
              <CreditCard className="w-3.5 h-3.5" />
              Stripe
              <ExternalLink className="w-3 h-3" />
            </a>
          )}
          <a
            href={firebaseAuthUsersUrl()}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => {
              e.stopPropagation();
              // The console has no per-user URL: copy the UID to paste into its search.
              void navigator.clipboard.writeText(userId).catch(() => {});
            }}
            title="Open Firebase Auth users (UID copied to clipboard for search)"
            className="flex items-center gap-1.5 px-2 py-1 bg-amber-500/20 text-amber-500 rounded text-xs font-medium hover:bg-amber-500/30 transition-colors"
          >
            <Flame className="w-3.5 h-3.5" />
            Firebase
            <ExternalLink className="w-3 h-3" />
          </a>
          <a
            href={`https://app.revenuecat.com/projects/${REVENUECAT_PROJECT_ID}/customers/${userId}`}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="flex items-center gap-1.5 px-2 py-1 bg-purple-500/20 text-purple-400 rounded text-xs font-medium hover:bg-purple-500/30 transition-colors"
          >
            <AppleIcon className="w-3.5 h-3.5" />
            RevenueCat
            <ExternalLink className="w-3 h-3" />
          </a>
          {user.email && (
            <a
              href={`https://fly.customer.io/workspaces/198592/journeys/people?email=${encodeURIComponent(user.email)}`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="flex items-center gap-1.5 px-2 py-1 bg-emerald-500/20 text-emerald-400 rounded text-xs font-medium hover:bg-emerald-500/30 transition-colors"
            >
              <Mail className="w-3.5 h-3.5" />
              Customer.io
              <ExternalLink className="w-3 h-3" />
            </a>
          )}
        </div>
        <div className="flex items-center gap-2">
          <RoleControl userId={userId} role={user.role ?? null} />
          <button
            onClick={(e) => {
              e.stopPropagation();
              setShowAuditLogModal(true);
            }}
            className="flex items-center gap-2 px-3 py-1.5 bg-base-content/10 text-base-content rounded-lg text-sm font-medium hover:bg-base-content/20 transition-colors"
          >
            <History className="w-4 h-4" />
            Audit log
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              setShowMergeModal(true);
            }}
            className="flex items-center gap-2 px-3 py-1.5 bg-primary-500/10 text-primary-500 rounded-lg text-sm font-medium hover:bg-primary-500/20 transition-colors"
          >
            <GitMerge className="w-4 h-4" />
            Merge into another user
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              setShowDeleteModal(true);
            }}
            className="flex items-center gap-2 px-3 py-1.5 bg-red-500/10 text-red-500 rounded-lg text-sm font-medium hover:bg-red-500/20 transition-colors"
          >
            <Trash2 className="w-4 h-4" />
            Delete User
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6 flex-wrap">
        {(["bookings", "payments", "profile", "devices", "feedback", "notes"] as const).map((tab) => {
          const feedbackCount = (user.postDinnerFeedback?.length ?? 0) + (user.reviews?.length ?? 0) + (user.generalFeedback?.length ?? 0);
          return (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                activeTab === tab
                  ? "bg-primary-500 text-white"
                  : "bg-dark-border text-dark-muted hover:bg-dark-border/70"
              }`}
            >
              {tab === "bookings" && `Bookings (${user.totalBookings})`}
              {tab === "payments" && `Payments (${user.payments.length})`}
              {tab === "profile" && "Profile Responses"}
              {tab === "devices" && `Devices (${(user.devices ?? []).length})`}
              {tab === "feedback" && `Feedback (${feedbackCount})`}
              {tab === "notes" && `Notes${userNotesCount > 0 ? ` (${userNotesCount})` : ""}`}
            </button>
          );
        })}
      </div>

      {/* Bookings Tab */}
      {activeTab === "bookings" && (
        <div className="space-y-3">
          {/* Create Reservation Button */}
          <div className="flex justify-end mb-2">
            <button
              onClick={() => setShowCreateReservationModal(true)}
              className="flex items-center gap-2 px-3 py-1.5 bg-primary-500/20 text-primary-400 rounded-lg text-sm font-medium hover:bg-primary-500/30 transition-colors"
            >
              <Plus className="w-4 h-4" />
              New Reservation
            </button>
          </div>
          
          {user.bookings.length === 0 ? (
            <p className="text-dark-muted text-sm">No bookings yet</p>
          ) : (
            user.bookings.map((booking) => {
              return (
                <div
                  key={booking.id}
                  className="p-4 bg-dark-border/30 rounded-lg"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-4 flex-1">
                      <div className="w-10 h-10 rounded-lg bg-primary-500/20 flex items-center justify-center flex-shrink-0">
                        <Calendar className="w-5 h-5 text-primary-500" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium">
                          {formatShortDate(booking.eventDate)}
                          {" at "}
                          {booking.eventTime}
                        </div>

                        <div className="text-sm text-dark-muted flex items-center gap-2 mt-1">
                          <MapPin className="w-3.5 h-3.5" />
                          {booking.neighborhoodName || "Unknown neighborhood"}
                          {booking.restaurantName && (
                            <>
                              <span className="mx-1">·</span>
                              {booking.tableId ? (
                                <a
                                  href={`/admin/matching/${booking.eventTimeId}?q=${encodeURIComponent(booking.tableId)}`}
                                  className="inline-flex items-center gap-1 hover:text-primary-500 hover:underline"
                                  title="Open this table on the matching page"
                                >
                                  {booking.restaurantName}
                                  <ExternalLink className="w-3 h-3" />
                                </a>
                              ) : (
                                booking.restaurantName
                              )}
                            </>
                          )}
                        </div>

                        {/* When the booking was created / cancelled, in the
                            event city's timezone */}
                        <div className="text-xs text-dark-muted flex flex-wrap items-center gap-x-3 gap-y-1 mt-1.5">
                          <span className="inline-flex items-center gap-1">
                            <Clock className="w-3.5 h-3.5" />
                            Booked {formatInEventZone(booking.createdAt, booking.eventTimezone)}
                          </span>
                          {(booking.status === "cancelled" || booking.cancelledAt) && (
                            <span className="inline-flex items-center gap-1 text-red-400">
                              <XCircle className="w-3.5 h-3.5" />
                              {booking.cancelledAt ? (
                                <>
                                  Cancelled{" "}
                                  {formatInEventZone(booking.cancelledAt, booking.eventTimezone)}
                                  {booking.eventStartsAt && (
                                    <span className="opacity-80">
                                      ·{" "}
                                      {formatRelativeToEvent(
                                        booking.cancelledAt,
                                        booking.eventStartsAt
                                      )}
                                    </span>
                                  )}
                                </>
                              ) : (
                                "Cancelled at an unknown time"
                              )}
                            </span>
                          )}
                        </div>

                        {/* Booking ID */}
                        <button
                          onClick={(e) => handleCopy(booking.id, e)}
                          className="flex items-center gap-1 text-xs text-dark-muted/70 mt-2 font-mono hover:text-dark-muted transition-colors"
                          title="Click to copy"
                        >
                          ID: {booking.id}
                          {copiedId === booking.id ? (
                            <Check className="w-3 h-3 text-green-500" />
                          ) : (
                            <Copy className="w-3 h-3" />
                          )}
                        </button>

                        {/* Cancellation Reason */}
                        {booking.status === "cancelled" && booking.cancellationReason && (
                          <div className="flex items-center gap-1.5 mt-2 text-xs text-red-400">
                            <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
                            <span>Reason: {booking.cancellationReason}</span>
                          </div>
                        )}

                        {/* Preferences */}
                        {booking.specialRequests && (() => {
                          try {
                            const prefs = JSON.parse(booking.specialRequests);
                            const tags: string[] = [];
                            if (prefs.budget?.length) tags.push(`Budget: ${prefs.budget.join(", ")}`);
                            if (prefs.age?.length) tags.push(`Age: ${prefs.age.map((a: string) => a.replace("_", " ")).join(", ")}`);
                            if (prefs.gender?.length) tags.push(`Gender: ${prefs.gender.join(", ")}`);
                            if (prefs.language?.length) tags.push(`Language: ${prefs.language.join(", ")}`);
                            if (prefs.preferSapphicTable) tags.push("Sapphic table");
                            if (prefs.specialRequestsText) tags.push(prefs.specialRequestsText);
                            if (tags.length === 0) return null;
                            return (
                              <div className="flex flex-wrap gap-1.5 mt-2">
                                {tags.map((tag, i) => (
                                  <span key={i} className="inline-flex items-center px-2 py-0.5 rounded-md bg-primary-500/10 text-primary-400 text-xs">
                                    {tag}
                                  </span>
                                ))}
                              </div>
                            );
                          } catch {
                            return (
                              <div className="text-xs text-dark-muted mt-2">
                                {booking.specialRequests}
                              </div>
                            );
                          }
                        })()}
                      </div>
                    </div>
                    
                    {/* Status and Actions */}
                    <div className="flex flex-col items-end gap-2">
                      <span
                        className={`px-3 py-1 rounded-full text-xs font-semibold ${
                          booking.status === "active"
                            ? "bg-green-500/20 text-green-500"
                            : booking.status === "cancelled"
                            ? "bg-red-500/20 text-red-500"
                            : booking.status === "cant_be_seated"
                            ? "bg-amber-500/20 text-amber-400"
                            : "bg-yellow-500/20 text-yellow-500"
                        }`}
                      >
                        {booking.status === "cant_be_seated"
                          ? "can't be seated"
                          : booking.status}
                      </span>

                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => setReassigningBooking(booking)}
                          className="p-1.5 bg-dark-border/50 text-dark-muted rounded-lg hover:bg-dark-border hover:text-white transition-colors"
                          title="Reassign table / mark can't be seated"
                        >
                          <ArrowRightLeft className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleOpenEditBooking(booking)}
                          className="p-1.5 bg-dark-border/50 text-dark-muted rounded-lg hover:bg-dark-border hover:text-white transition-colors"
                          title="Edit booking"
                        >
                          <Edit3 className="w-4 h-4" />
                        </button>
                        {booking.status === "active" &&
                          booking.eventStartsAt &&
                          new Date(booking.eventStartsAt) < new Date() && (
                            <button
                              onClick={async () => {
                                const ok = await confirm({
                                  title: "Flag this booking as a no-show?",
                                  message:
                                    "This opens a no-show case. Depending on the current mode the guest may be emailed and, after the grace period, charged the no-show fee. You can excuse the case any time before that on the No-shows page.",
                                  confirmText: "Flag as no-show",
                                  type: "danger",
                                });
                                if (!ok) return;
                                openNoShowCase.mutate(booking.id, {
                                  onSuccess: () => toast.success("Opened a no-show case"),
                                  onError: (err) =>
                                    toast.error(err instanceof Error ? err.message : "Failed to open no-show case"),
                                });
                              }}
                              className="p-1.5 bg-dark-border/50 text-dark-muted rounded-lg hover:bg-red-500/20 hover:text-red-400 transition-colors"
                              title="Flag as no-show"
                            >
                              <UserX className="w-4 h-4" />
                            </button>
                          )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}

      {/* Payments Tab */}
      {activeTab === "payments" && (
        <div className="space-y-3">
          {user.payments.length === 0 ? (
            <p className="text-dark-muted text-sm">No payments yet</p>
          ) : (
            user.payments.map((payment) => (
              <div
                key={payment.id}
                className="flex items-center justify-between p-4 bg-dark-border/30 rounded-lg"
              >
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-lg bg-accent-500/20 flex items-center justify-center">
                    {getPaymentProcessorIcon(payment.paymentProcessor || "STRIPE")}
                  </div>
                  <div>
                    <div className="font-medium">
                      ${parseFloat(payment.amount).toFixed(2)} {payment.currency.toUpperCase()}
                    </div>
                    <div className="text-sm text-dark-muted">
                      {payment.type === "subscription" ? "Subscription" : "One-time"} ·{" "}
                      {new Date(payment.createdAt).toLocaleDateString()}
                    </div>
                  </div>
                </div>
                <span
                  className={`px-3 py-1 rounded-full text-xs font-semibold ${
                    payment.status === "succeeded"
                      ? "bg-green-500/20 text-green-500"
                      : payment.status === "failed"
                      ? "bg-red-500/20 text-red-500"
                      : "bg-yellow-500/20 text-yellow-500"
                  }`}
                >
                  {payment.status}
                </span>
              </div>
            ))
          )}

          {/* Entitlements */}
          <div className="mt-6">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-semibold text-dark-muted">Entitlements</h4>
              <div className="flex items-center gap-2">
                {extendableEntitlement && (
                  <button
                    onClick={() => setShowExtendSubscriptionModal(true)}
                    disabled={extendSubscriptionMutation.isPending}
                    className="flex items-center gap-2 px-3 py-1.5 bg-blue-500/20 text-blue-400 rounded-lg text-sm font-medium hover:bg-blue-500/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <CalendarClock className="w-4 h-4" />
                    Extend Subscription
                  </button>
                )}
                <button
                  onClick={handleRefreshEntitlements}
                  disabled={refreshEntitlementsMutation.isPending}
                  className="flex items-center gap-2 px-3 py-1.5 bg-purple-500/20 text-purple-400 rounded-lg text-sm font-medium hover:bg-purple-500/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {refreshEntitlementsMutation.isPending ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      Refreshing...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="w-4 h-4" />
                      Refresh Entitlements
                    </>
                  )}
                </button>
                <button
                  onClick={handleGrantEntitlement}
                  disabled={grantEntitlementMutation.isPending}
                  className="flex items-center gap-2 px-3 py-1.5 bg-primary-500/20 text-primary-400 rounded-lg text-sm font-medium hover:bg-primary-500/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {grantEntitlementMutation.isPending ? (
                    <>
                      <div className="w-4 h-4 border-2 border-primary-400 border-t-transparent rounded-full animate-spin" />
                      Granting...
                    </>
                  ) : (
                    <>
                      <Gift className="w-4 h-4" />
                      Grant One-Time
                    </>
                  )}
                </button>
              </div>
            </div>
            {user.entitlements.length === 0 ? (
              <p className="text-dark-muted text-sm">No entitlements yet</p>
            ) : (
              user.entitlements.map((ent) => {
                const isEditing = editingEntitlementId === ent.id;
                return (
                  <div
                    key={ent.id}
                    className={`flex items-center justify-between p-4 bg-dark-border/30 rounded-lg mb-2 ${isEditing ? "ring-2 ring-primary-500" : ""}`}
                  >
                    <div>
                      <div className="font-medium capitalize">
                        {ent.type}
                      </div>
                      <div className="text-xs text-dark-muted font-mono break-all">
                        {ent.id}
                      </div>
                      {ent.revenuecatTransactionId && (
                        <div className="text-xs text-dark-muted font-mono break-all">
                          RC: {ent.revenuecatTransactionId}
                        </div>
                      )}
                      {ent.stripeSubscriptionId && (
                        <div className="text-xs text-dark-muted font-mono break-all">
                          Stripe sub: {ent.stripeSubscriptionId}
                        </div>
                      )}
                      {ent.stripePaymentId && (
                        <div className="text-xs text-dark-muted font-mono break-all">
                          Stripe pmt: {ent.stripePaymentId}
                        </div>
                      )}
                      <div className="text-sm text-dark-muted">
                        {ent.begin && `From ${new Date(ent.begin).toLocaleDateString()}`}
                        {ent.end && ` to ${new Date(ent.end).toLocaleDateString()}`}
                        {ent.renewsAt && ` · Renews ${new Date(ent.renewsAt).toLocaleDateString()}`}
                        {!ent.begin && !ent.end && !ent.renewsAt && "No expiration"}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {isEditing ? (
                        <>
                          <select
                            value={editingEntitlementStatus}
                            onChange={(e) => setEditingEntitlementStatus(e.target.value)}
                            className={`px-3 py-1.5 rounded-lg text-xs font-semibold border-0 focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                              editingEntitlementStatus === "active"
                                ? "bg-green-500/20 text-green-500"
                                : editingEntitlementStatus === "cancelled" || editingEntitlementStatus === "consumed"
                                ? "bg-red-500/20 text-red-500"
                                : "bg-yellow-500/20 text-yellow-500"
                            }`}
                          >
                            <option value="active">active</option>
                            <option value="consumed">consumed</option>
                            <option value="cancelled">cancelled</option>
                            <option value="pending">pending</option>
                            <option value="paused">paused</option>
                            <option value="past_due">past_due</option>
                          </select>
                          <button
                            onClick={() => handleSaveEntitlement(ent.id)}
                            disabled={updateEntitlementMutation.isPending}
                            className="p-1.5 bg-green-500/20 text-green-500 rounded-lg hover:bg-green-500/30 transition-colors disabled:opacity-50"
                            title="Save changes"
                          >
                            {updateEntitlementMutation.isPending ? (
                              <div className="w-4 h-4 border-2 border-green-500 border-t-transparent rounded-full animate-spin" />
                            ) : (
                              <Save className="w-4 h-4" />
                            )}
                          </button>
                          <button
                            onClick={handleCancelEditEntitlement}
                            disabled={updateEntitlementMutation.isPending}
                            className="p-1.5 bg-dark-border text-dark-muted rounded-lg hover:bg-dark-border/70 transition-colors disabled:opacity-50"
                            title="Cancel"
                          >
                            <XCircle className="w-4 h-4" />
                          </button>
                        </>
                      ) : (
                        <>
                          <span
                            className={`px-3 py-1 rounded-full text-xs font-semibold ${
                              ent.status === "active"
                                ? "bg-green-500/20 text-green-500"
                                : ent.status === "cancelled" || ent.status === "consumed"
                                ? "bg-red-500/20 text-red-500"
                                : "bg-yellow-500/20 text-yellow-500"
                            }`}
                          >
                            {ent.status}
                          </span>
                          <button
                            onClick={() => handleStartEditEntitlement(ent)}
                            className="p-1.5 bg-dark-border/50 text-dark-muted rounded-lg hover:bg-dark-border hover:text-white transition-colors"
                            title="Edit entitlement"
                          >
                            <Edit3 className="w-4 h-4" />
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}

      {/* Profile Tab */}
      {activeTab === "profile" && (
        <div>
          {!user.quizAnswers ? (
            <p className="text-dark-muted text-sm">Quiz not completed</p>
          ) : (
            <div className="grid md:grid-cols-2 gap-4">
              {quizQuestions.map((question) => {
                const answer = user.quizAnswers?.[question.id];
                if (!answer) return null;
                return (
                  <div
                    key={question.id}
                    className="p-4 bg-dark-border/30 rounded-lg"
                  >
                    <div className="text-xs text-dark-muted mb-1">{question.category}</div>
                    <div className="text-sm font-medium mb-2">{question.question}</div>
                    <div className="text-sm text-primary-400">
                      {formatQuizAnswer(question.id, answer, quizQuestions)}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Devices Tab */}
      {activeTab === "devices" && (
        <div className="space-y-3">
          {(user.devices ?? []).length === 0 ? (
            <p className="text-dark-muted text-sm">No devices registered</p>
          ) : (
            <div className="overflow-x-auto rounded-lg border border-dark-border">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-dark-border/30 border-b border-dark-border">
                    <th className="text-left py-3 px-4 font-medium">Platform</th>
                    <th className="text-left py-3 px-4 font-medium">Device info</th>
                    <th className="text-left py-3 px-4 font-medium">Last seen</th>
                    <th className="text-left py-3 px-4 font-medium">Registered</th>
                  </tr>
                </thead>
                <tbody>
                  {(user.devices ?? []).map((device) => (
                    <tr key={device.id} className="border-b border-dark-border/50 last:border-0">
                      <td className="py-3 px-4">
                        <span className="inline-flex items-center gap-1.5">
                          <Smartphone className="w-4 h-4 text-dark-muted" />
                          {device.platform ?? "—"}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-dark-muted font-mono text-xs max-w-[280px] truncate" title={device.deviceInfo ?? undefined}>
                        {device.deviceInfo ?? "—"}
                      </td>
                      <td className="py-3 px-4 text-dark-muted">
                        {formatShortDateTime(device.lastSeen)}
                      </td>
                      <td className="py-3 px-4 text-dark-muted">
                        {formatShortDateTime(device.createdAt)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Feedback Tab */}
      {activeTab === "feedback" && (
        <div className="space-y-6">
          {/* Post-Dinner Survey Responses */}
          <div>
            <h4 className="text-sm font-semibold text-dark-muted mb-3 flex items-center gap-2">
              <MessageSquare className="w-4 h-4" />
              Post-Dinner Surveys ({user.postDinnerFeedback?.length ?? 0})
            </h4>
            {(user.postDinnerFeedback?.length ?? 0) === 0 ? (
              <p className="text-dark-muted text-sm">No post-dinner survey responses</p>
            ) : (
              <div className="space-y-3">
                {user.postDinnerFeedback!.map((fb) => (
                  <div key={fb.id} className="p-4 bg-dark-border/30 rounded-lg">
                    <div className="flex items-start justify-between gap-4 mb-3">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-primary-500/20 flex items-center justify-center flex-shrink-0">
                          <MessageSquare className="w-5 h-5 text-primary-500" />
                        </div>
                        <div>
                          <div className="font-medium">
                            {fb.eventDate ? formatShortDate(fb.eventDate) : "Unknown date"}
                            {fb.eventTime && ` at ${fb.eventTime}`}
                          </div>
                          {fb.restaurantName && (
                            <div className="text-sm text-dark-muted">{fb.restaurantName}</div>
                          )}
                        </div>
                      </div>
                      <div className="text-xs text-dark-muted">
                        {formatShortDateTime(fb.createdAt)}
                      </div>
                    </div>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                      {fb.overallExperience != null && (
                        <div className="px-3 py-2 bg-dark-bg/50 rounded-lg">
                          <div className="text-xs text-dark-muted mb-1">Overall Experience</div>
                          <div className="font-medium text-primary-400">{fb.overallExperience}/5</div>
                        </div>
                      )}
                      {fb.recommendationScore != null && (
                        <div className="px-3 py-2 bg-dark-bg/50 rounded-lg">
                          <div className="text-xs text-dark-muted mb-1">NPS Score</div>
                          <div className={`font-medium ${
                            fb.recommendationScore >= 9 ? "text-green-400" :
                            fb.recommendationScore >= 7 ? "text-yellow-400" : "text-red-400"
                          }`}>
                            {fb.recommendationScore}/10
                          </div>
                        </div>
                      )}
                      {fb.feltWelcomed && (
                        <div className="px-3 py-2 bg-dark-bg/50 rounded-lg">
                          <div className="text-xs text-dark-muted mb-1">Felt Welcomed</div>
                          <div className={`font-medium ${fb.feltWelcomed === "yes" ? "text-green-400" : "text-red-400"}`}>
                            {fb.feltWelcomed === "yes" ? "Yes" : "No"}
                          </div>
                        </div>
                      )}
                      {fb.restaurantSelection && (
                        <div className="px-3 py-2 bg-dark-bg/50 rounded-lg">
                          <div className="text-xs text-dark-muted mb-1">Restaurant Rating</div>
                          <div className="font-medium capitalize text-primary-400">{fb.restaurantSelection}</div>
                        </div>
                      )}
                      {fb.futureAttendance && (
                        <div className="px-3 py-2 bg-dark-bg/50 rounded-lg">
                          <div className="text-xs text-dark-muted mb-1">Would Attend Again</div>
                          <div className={`font-medium ${fb.futureAttendance === "yes" ? "text-green-400" : "text-red-400"}`}>
                            {fb.futureAttendance === "yes" ? "Yes" : "No"}
                          </div>
                        </div>
                      )}
                    </div>
                    {fb.notes && (
                      <div className="mt-3 px-3 py-2 bg-dark-bg/50 rounded-lg">
                        <div className="text-xs text-dark-muted mb-1">Notes</div>
                        <div className="text-sm">{fb.notes}</div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Reviews */}
          <div>
            <h4 className="text-sm font-semibold text-dark-muted mb-3 flex items-center gap-2">
              <Star className="w-4 h-4" />
              Reviews ({user.reviews?.length ?? 0})
            </h4>
            {(user.reviews?.length ?? 0) === 0 ? (
              <p className="text-dark-muted text-sm">No reviews</p>
            ) : (
              <div className="space-y-3">
                {user.reviews!.map((review) => (
                  <div key={review.id} className="p-4 bg-dark-border/30 rounded-lg">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-yellow-500/20 flex items-center justify-center flex-shrink-0">
                          <Star className="w-5 h-5 text-yellow-500" />
                        </div>
                        <div>
                          <div className="font-medium flex items-center gap-2">
                            {review.rating != null && (
                              <span className="flex items-center gap-1">
                                {Array.from({ length: 5 }).map((_, i) => (
                                  <Star
                                    key={i}
                                    className={`w-4 h-4 ${i < review.rating! ? "text-yellow-500 fill-yellow-500" : "text-dark-border"}`}
                                  />
                                ))}
                              </span>
                            )}
                          </div>
                          <div className="text-sm text-dark-muted">
                            {review.eventDate ? formatShortDate(review.eventDate) : "Unknown date"}
                            {review.eventTime && ` at ${review.eventTime}`}
                            {review.restaurantName && ` · ${review.restaurantName}`}
                          </div>
                        </div>
                      </div>
                      <div className="text-xs text-dark-muted">
                        {formatShortDateTime(review.createdAt)}
                      </div>
                    </div>
                    {review.comment && (
                      <div className="mt-3 px-3 py-2 bg-dark-bg/50 rounded-lg text-sm">
                        {review.comment}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* General Feedback */}
          <div>
            <h4 className="text-sm font-semibold text-dark-muted mb-3 flex items-center gap-2">
              <MessageSquare className="w-4 h-4" />
              General Feedback ({user.generalFeedback?.length ?? 0})
            </h4>
            {(user.generalFeedback?.length ?? 0) === 0 ? (
              <p className="text-dark-muted text-sm">No general feedback submitted</p>
            ) : (
              <div className="space-y-3">
                {user.generalFeedback!.map((fb) => (
                  <div key={fb.id} className="p-4 bg-dark-border/30 rounded-lg">
                    <div className="flex items-start justify-between gap-4">
                      <div className="text-sm flex-1">{fb.feedback}</div>
                      <div className="text-xs text-dark-muted whitespace-nowrap">
                        {formatShortDateTime(fb.createdAt)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Notes Tab — internal staff notes, never visible to the user */}
      {activeTab === "notes" && (
        <div className="max-w-xl">
          <UserNotesSection userId={userId} notes={userNotesData?.data ?? []} />
        </div>
      )}

      {/* Delete User Modal */}
      <AnimatePresence>
        {showDeleteModal && (
          <DeleteUserModal
            user={{
              id: user.id,
              firstName: user.firstName,
              lastName: user.lastName,
              email: user.email,
            }}
            onClose={() => setShowDeleteModal(false)}
            onConfirm={handleDeleteUser}
            isDeleting={deleteUserMutation.isPending}
          />
        )}
      </AnimatePresence>

      {/* Create Reservation Modal */}
      <AnimatePresence>
        {showCreateReservationModal && (
          <CreateReservationModal
            userId={userId}
            onClose={() => setShowCreateReservationModal(false)}
            onSuccess={() => refetch()}
          />
        )}
      </AnimatePresence>

      {/* Extend Subscription Modal */}
      <AnimatePresence>
        {showExtendSubscriptionModal && extendableEntitlement && (
          <ExtendSubscriptionModal
            user={{ firstName: user.firstName, lastName: user.lastName }}
            currentRenewalAt={extendableEntitlement.renewsAt}
            onClose={() => setShowExtendSubscriptionModal(false)}
            onConfirm={handleExtendSubscription}
            isExtending={extendSubscriptionMutation.isPending}
          />
        )}
      </AnimatePresence>

      {/* Merge User Modal */}
      <AnimatePresence>
        {showMergeModal && (
          <MergeUserModal
            sourceUser={{
              id: user.id,
              firstName: user.firstName,
              lastName: user.lastName,
              email: user.email,
            }}
            onClose={() => setShowMergeModal(false)}
            onSuccess={() => refetch()}
          />
        )}
      </AnimatePresence>

      {/* Edit Booking Modal — booking-level fields only.
          Table reassignment is delegated to ReassignmentDialog. */}
      {editingBooking && (
        <EditReservationModal
          isOpen={!!editingBooking}
          onClose={handleCloseEditBooking}
          onSubmit={handleEditBookingSubmit}
          eventTimes={(eventsData?.data || []).map((e: any) => ({
            id: e.id,
            date: e.date,
            time: e.time,
          }))}
          neighborhoods={neighborhoods.map((n) => ({
            id: n.id,
            name: n.name,
          }))}
          onReassign={() => {
            setReassigningBooking(editingBooking);
            setEditingBooking(null);
          }}
          showStatus
          initialData={{
            userName: `${user.firstName} ${user.lastName || ""}`.trim(),
            userEmail: user.email || "",
            eventTimeId: editingBooking.eventTimeId,
            neighborhoodId: editingBooking.neighborhoodId || "",
            status: editingBooking.status,
            bookingId: editingBooking.id,
          }}
          isLoading={updateBookingMutation.isPending}
        />
      )}

      {/* Reassignment Dialog */}
      <ReassignmentDialog
        isOpen={!!reassigningBooking}
        bookingId={reassigningBooking?.id ?? null}
        eventTimeId={reassigningBooking?.eventTimeId}
        onClose={() => setReassigningBooking(null)}
        onSuccess={() => refetch()}
      />

      {/* Audit log viewer — scoped to this user (as subject of the action). */}
      <AuditLogModal
        isOpen={showAuditLogModal}
        onClose={() => setShowAuditLogModal(false)}
        baseFilters={{ subjectUserId: userId }}
        contextLabel={`User • ${[user.firstName, user.lastName].filter(Boolean).join(" ") || userId}`}
      />
    </div>
  );
}

const ITEMS_PER_PAGE_OPTIONS = [25, 50, 100] as const;

export default function AdminUsers() {
  // URL search params for search query
  const [searchParams, setSearchParams] = useSearchParams();
  const urlSearchQuery = searchParams.get("q") || "";

  // UI state
  const [searchInput, setSearchInput] = useState(urlSearchQuery);
  const [searchQuery, setSearchQuery] = useState(urlSearchQuery);
  const [sortField, setSortField] = useState<SortField>("createdAt");
  const [sortOrder, setSortOrder] = useState<SortOrder>("desc");
  const [subscriptionFilter, setSubscriptionFilter] = useState<"all" | "active" | "none">("all");
  const [roleFilter, setRoleFilter] = useState<"all" | "staff">("all");
  const [expandedUserId, setExpandedUserId] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState<typeof ITEMS_PER_PAGE_OPTIONS[number]>(25);

  // Sync search state with URL params
  useEffect(() => {
    setSearchInput(urlSearchQuery);
    setSearchQuery(urlSearchQuery);
  }, [urlSearchQuery]);

  // Handle search submission
  const handleSearch = () => {
    setSearchQuery(searchInput);
    setCurrentPage(1);
    // Update URL with search query
    if (searchInput) {
      setSearchParams({ q: searchInput });
    } else {
      setSearchParams({});
    }
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  const handleClearSearch = () => {
    setSearchInput("");
    setSearchQuery("");
    setCurrentPage(1);
    setSearchParams({});
  };

  // Fetch paginated data from API
  const { data: usersData, isLoading } = useAdminUsersDetailed({
    page: currentPage,
    limit: itemsPerPage,
    search: searchQuery,
    sortField,
    sortOrder,
    subscriptionFilter,
    roleFilter,
  });

  // Extract data from API response
  const users = useMemo(() => usersData?.data?.users || [], [usersData?.data?.users]);
  const pagination = usersData?.data?.pagination;
  const stats = usersData?.data?.stats;

  // Pagination values from API
  const totalFilteredUsers = pagination?.total || 0;
  const totalPages = pagination?.totalPages || 1;
  const startIndex = ((pagination?.page || 1) - 1) * (pagination?.limit || itemsPerPage);
  const endIndex = Math.min(startIndex + (pagination?.limit || itemsPerPage), totalFilteredUsers);

  // Handlers
  const handleSubscriptionFilterChange = (filter: "all" | "active" | "none") => {
    setSubscriptionFilter(filter);
    setCurrentPage(1);
  };

  const handleItemsPerPageChange = (value: typeof ITEMS_PER_PAGE_OPTIONS[number]) => {
    setItemsPerPage(value);
    setCurrentPage(1);
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortOrder("desc");
    }
    setCurrentPage(1);
  };

  const toggleExpand = (userId: string) => {
    setExpandedUserId(expandedUserId === userId ? null : userId);
  };

  // Stats from API (full dataset, not just current page)
  const totalUsers = stats?.totalUsers || 0;
  const usersWithSubscription = stats?.usersWithSubscription || 0;
  const usersWithBookings = stats?.usersWithBookings || 0;
  const quizCompleted = stats?.quizCompleted || 0;

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <h1 className="text-4xl md:text-5xl font-bold mb-2">Users</h1>
        <p className="text-dark-muted text-lg">Browse and manage user accounts</p>
      </motion.div>

      {/* Stats Grid */}
      <div className="grid md:grid-cols-4 gap-6 mb-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="card"
        >
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 rounded-xl bg-primary-500/20 flex items-center justify-center">
              <Users className="w-6 h-6 text-primary-500" />
            </div>
            <div>
              <p className="text-2xl font-bold">{totalUsers}</p>
              <p className="text-dark-muted text-sm">Total Users</p>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="card"
        >
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 rounded-xl bg-green-500/20 flex items-center justify-center">
              <CreditCard className="w-6 h-6 text-green-500" />
            </div>
            <div>
              <p className="text-2xl font-bold">{usersWithSubscription}</p>
              <p className="text-dark-muted text-sm">Active Subscribers</p>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="card"
        >
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 rounded-xl bg-accent-500/20 flex items-center justify-center">
              <Calendar className="w-6 h-6 text-accent-500" />
            </div>
            <div>
              <p className="text-2xl font-bold">{usersWithBookings}</p>
              <p className="text-dark-muted text-sm">Users with Bookings</p>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="card"
        >
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 rounded-xl bg-primary-500/20 flex items-center justify-center">
              <UserCircle className="w-6 h-6 text-primary-500" />
            </div>
            <div>
              <p className="text-2xl font-bold">{quizCompleted}</p>
              <p className="text-dark-muted text-sm">Quiz Completed</p>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Search and Filters */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        className="card mb-6"
      >
        <div className="flex flex-col md:flex-row md:items-center gap-4">
          {/* Search */}
          <div className="relative flex-1 flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-dark-muted" />
              <input
                type="text"
                placeholder="Search by name, email, phone, Instagram, or city..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={handleSearchKeyDown}
                className="w-full pl-10 pr-4 py-3 bg-dark-border/50 border border-dark-border rounded-lg text-sm focus:outline-none focus:border-primary-500 transition-colors"
              />
              {searchInput && (
                <button
                  onClick={handleClearSearch}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-dark-muted hover:text-white transition-colors"
                  title="Clear search"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
            <button
              onClick={handleSearch}
              className="px-4 py-2 bg-primary-500 text-white rounded-lg text-sm font-medium hover:bg-primary-600 transition-colors flex items-center gap-2"
            >
              <Search className="w-4 h-4" />
              Search
            </button>
          </div>

          {/* Subscription Filter */}
          <div className="flex gap-2">
            {(["all", "active", "none"] as const).map((filter) => (
              <button
                key={filter}
                onClick={() => handleSubscriptionFilterChange(filter)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  subscriptionFilter === filter
                    ? "bg-primary-500 text-white"
                    : "bg-dark-border text-dark-muted hover:bg-dark-border/70"
                }`}
              >
                {filter === "all" && "All"}
                {filter === "active" && "Subscribers"}
                {filter === "none" && "Non-Subscribers"}
              </button>
            ))}
          </div>

          {/* Role Filter */}
          <button
            onClick={() => {
              setRoleFilter((prev) => (prev === "staff" ? "all" : "staff"));
              setCurrentPage(1);
            }}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              roleFilter === "staff"
                ? "bg-primary-500 text-white"
                : "bg-dark-border text-dark-muted hover:bg-dark-border/70"
            }`}
            title="Show only users with a staff role"
          >
            <Shield className="w-4 h-4" />
            Staff
          </button>
        </div>
      </motion.div>

      {/* Users Table */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
        className="card overflow-hidden"
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-2xl font-bold flex items-center gap-3">
            {totalFilteredUsers} User{totalFilteredUsers !== 1 ? "s" : ""}
            {isLoading && (
              <Loader size={20} />
            )}
          </h2>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-dark-border text-left">
                <th className="pb-3 pr-4 w-8"></th>
                <th
                  className="pb-3 text-dark-muted font-medium text-sm cursor-pointer hover:text-white transition-colors"
                  onClick={() => handleSort("firstName")}
                >
                  <div className="flex items-center gap-1">
                    User
                    <ArrowUpDown className="w-3.5 h-3.5" />
                  </div>
                </th>
                <th className="pb-3 text-dark-muted font-medium text-sm">Contact</th>
                <th className="pb-3 text-dark-muted font-medium text-sm">City</th>
                <th
                  className="pb-3 text-dark-muted font-medium text-sm cursor-pointer hover:text-white transition-colors"
                  onClick={() => handleSort("totalBookings")}
                >
                  <div className="flex items-center gap-1">
                    Bookings
                    <ArrowUpDown className="w-3.5 h-3.5" />
                  </div>
                </th>
                <th
                  className="pb-3 text-dark-muted font-medium text-sm cursor-pointer hover:text-white transition-colors"
                  onClick={() => handleSort("hasActiveSubscription")}
                >
                  <div className="flex items-center gap-1">
                    Subscription
                    <ArrowUpDown className="w-3.5 h-3.5" />
                  </div>
                </th>
                <th
                  className="pb-3 text-dark-muted font-medium text-sm cursor-pointer hover:text-white transition-colors"
                  onClick={() => handleSort("createdAt")}
                >
                  <div className="flex items-center gap-1">
                    Joined
                    <ArrowUpDown className="w-3.5 h-3.5" />
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={7} className="py-16">
                    <div className="flex flex-col items-center justify-center gap-3">
                      <Loader />
                      <span className="text-dark-muted text-sm">Loading users...</span>
                    </div>
                  </td>
                </tr>
              ) : users.length === 0 ? (
                <tr>
                  <td colSpan={7} className="pt-8 text-center text-dark-muted">
                    No users found
                  </td>
                </tr>
              ) : (
                users.map((user, index) => (
                  <UserRow
                    key={user.id}
                    user={user}
                    index={index}
                    isExpanded={expandedUserId === user.id}
                    onToggleExpand={() => toggleExpand(user.id)}
                  />
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination Controls */}
        {totalFilteredUsers > 0 && (
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-6 pt-6 border-t border-dark-border">
            {/* Items per page selector */}
            <div className="flex items-center gap-3">
              <span className="text-sm text-dark-muted">Show</span>
              <div className="flex gap-1">
                {ITEMS_PER_PAGE_OPTIONS.map((option) => (
                  <button
                    key={option}
                    onClick={() => handleItemsPerPageChange(option)}
                    className={`px-3 py-1.5 rounded text-sm font-medium transition-colors ${
                      itemsPerPage === option
                        ? "bg-primary-500 text-white"
                        : "bg-dark-border text-dark-muted hover:bg-dark-border/70"
                    }`}
                  >
                    {option}
                  </button>
                ))}
              </div>
              <span className="text-sm text-dark-muted">per page</span>
            </div>

            {/* Page info and navigation */}
            <div className="flex items-center gap-4">
              <span className="text-sm text-dark-muted">
                {startIndex + 1}–{endIndex} of {totalFilteredUsers}
              </span>

              <div className="flex items-center gap-1">
                <button
                  onClick={() => setCurrentPage(1)}
                  disabled={currentPage === 1}
                  className="p-2 rounded-lg bg-dark-border text-dark-muted hover:bg-dark-border/70 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  title="First page"
                >
                  <ChevronsLeft className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="p-2 rounded-lg bg-dark-border text-dark-muted hover:bg-dark-border/70 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  title="Previous page"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>

                {/* Page number buttons */}
                <div className="flex items-center gap-1 mx-2">
                  {Array.from({ length: totalPages }, (_, i) => i + 1)
                    .filter((page) => {
                      // Show first page, last page, current page, and pages around current
                      return (
                        page === 1 ||
                        page === totalPages ||
                        Math.abs(page - currentPage) <= 1
                      );
                    })
                    .map((page, index, arr) => {
                      // Add ellipsis
                      const showEllipsisBefore = index > 0 && page - arr[index - 1] > 1;
                      return (
                        <span key={page} className="flex items-center">
                          {showEllipsisBefore && (
                            <span className="px-2 text-dark-muted">…</span>
                          )}
                          <button
                            onClick={() => setCurrentPage(page)}
                            className={`min-w-[36px] h-9 rounded-lg text-sm font-medium transition-colors ${
                              currentPage === page
                                ? "bg-primary-500 text-white"
                                : "bg-dark-border text-dark-muted hover:bg-dark-border/70"
                            }`}
                          >
                            {page}
                          </button>
                        </span>
                      );
                    })}
                </div>

                <button
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="p-2 rounded-lg bg-dark-border text-dark-muted hover:bg-dark-border/70 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  title="Next page"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setCurrentPage(totalPages)}
                  disabled={currentPage === totalPages}
                  className="p-2 rounded-lg bg-dark-border text-dark-muted hover:bg-dark-border/70 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  title="Last page"
                >
                  <ChevronsRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        )}
      </motion.div>
    </div>
  );
}

// Separated user row component for better performance
function UserRow({
  user,
  index,
  isExpanded,
  onToggleExpand,
}: {
  user: AdminAPI.AdminUserSummary;
  index: number;
  isExpanded: boolean;
  onToggleExpand: () => void;
}) {
  const toast = useToast();

  const handleCopy = async (value: string, label: string, e: React.MouseEvent) => {
    e.stopPropagation();
    await navigator.clipboard.writeText(value);
    toast.success(`Copied ${label}`);
  };

  return (
    <>
      <motion.tr
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: Math.min(index * 0.02, 0.5) }}
        className={`border-b border-divider hover:bg-white/20 dark:hover:bg-neutral-800/50 transition-colors cursor-pointer`}
        onClick={onToggleExpand}
      >
        <td className="py-4 pr-4">
          <button className="text-dark-muted hover:text-white transition-colors">
            {isExpanded ? (
              <ChevronUp className="w-5 h-5" />
            ) : (
              <ChevronDown className="w-5 h-5" />
            )}
          </button>
        </td>
        <td className="py-4">
          <div className="flex items-center gap-3">
            <ProfileImage
              size="md"
              user={{
                firstName: user.firstName,
                lastName: user.lastName,
                profileImageUrl: user.profileImageUrl,
              }}
            />
            <div>
              <div className="font-medium flex items-center gap-1.5">
                {user.firstName} {user.lastName}
                {user.role && <RoleBadge role={user.role} className="w-3.5 h-3.5" />}
              </div>
              <div className="text-dark-muted text-xs truncate max-w-[200px]">
                {user.email || `@${user.username}`}
              </div>
            </div>
          </div>
        </td>
        <td className="py-4">
          <div className="flex items-center gap-3 text-sm">
            {user.email && (
              <button
                onClick={(e) => handleCopy(user.email!, "email", e)}
                className="text-dark-muted hover:text-primary-500 transition-colors"
                title={user.email}
              >
                <Mail className="w-4 h-4" />
              </button>
            )}
            {user.phone && (
              <>
              <button
                onClick={(e) => handleCopy(user.phone!, "phone", e)}
                className="text-dark-muted hover:text-primary-500 transition-colors"
                title={user.phone}
              >
                <Phone className="w-4 h-4" />
              </button>

              {/* Send Message Button */}
              <SendMessageButton
                userIds={[user.id]}
                recipientLabel={`${user.firstName} ${user.lastName || ''}`.trim()}
                recipientDetails={user.email || undefined}
                variant="icon"
              />
              </>
            )}
            {user.instagram && (
              <a
                href={`https://instagram.com/${user.instagram.replace("@", "")}`}
                onClick={(e) => e.stopPropagation()}
                target="_blank"
                rel="noopener noreferrer"
                className="text-dark-muted hover:text-primary-500 transition-colors"
                title={`@${user.instagram.replace("@", "")}`}
              >
                <Instagram className="w-4 h-4" />
              </a>
            )}

          </div>
        </td>
        <td className="py-4 text-sm">
          {user.city ? (
            <span className="text-dark-muted">{user.city}</span>
          ) : (
            <span className="text-dark-muted/50">—</span>
          )}
        </td>
        <td className="py-4">
          <div className="flex items-center gap-2">
            <span className="font-semibold">{user.totalBookings}</span>
            {user.activeBookings > 0 && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-green-500/20 text-green-500">
                {user.activeBookings} active
              </span>
            )}
          </div>
        </td>
        <td className="py-4">
          {user.hasActiveSubscription ? (
            <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold bg-green-500/20 text-green-500">
              <Check className="w-3.5 h-3.5" />
              Active
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold bg-dark-border text-dark-muted">
              <X className="w-3.5 h-3.5" />
              None
            </span>
          )}
        </td>
        <td className="py-4 text-sm text-dark-muted">
          {new Date(user.createdAt).toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric",
          })}
        </td>
      </motion.tr>

      {/* Expanded Details */}
      <AnimatePresence>
        {isExpanded && (
          <motion.tr
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
          >
            <td colSpan={7} className="p-0">
              <UserDetailPanel userId={user.id} onUserDeleted={onToggleExpand} />
            </td>
          </motion.tr>
        )}
      </AnimatePresence>
    </>
  );
}

